import React, { forwardRef } from "react";
import { cn } from "../utils/cn";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      label,
      error,
      helperText,
      fullWidth = true,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputId = props.id || `input-${typeof label === "string" ? label.toLowerCase().replace(/\s+/g, "-") : `input-${Math.random().toString(36).substring(7)}`}`;

    return (
      <div className={cn("flex flex-col gap-2", fullWidth && "w-full")}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "text-base md:text-lg font-medium transition-colors",
              "text-app dark:text-gray-200",
              error && "text-red-600 dark:text-red-400"
            )}
          >
            {label}
          </label>
        )}
        <input
          id={inputId}
          type={type}
          ref={ref}
          disabled={disabled}
          className={cn(
            // Base styles
            "flex h-10 w-full rounded-md border-2 px-4 py-2 text-sm",
            "bg-white dark:bg-gray-700",
            "transition-all duration-200",
            "placeholder:text-gray-400 dark:placeholder:text-gray-500",
            
            // Border styles - using app border color
            "border-app dark:border-gray-600",
            "focus:border-theme focus:ring-2 focus:ring-theme/20 dark:focus:ring-theme/30",
            
            // Text styles - using app text color
            "text-app dark:text-gray-100",
            "caret-theme",
            
            // Disabled styles
            "disabled:cursor-not-allowed disabled:opacity-60",
            "disabled:bg-gray-50 dark:disabled:bg-gray-800",
            
            // Error styles
            error && "border-red-500 dark:border-red-400",
            error && "focus:border-red-500 focus:ring-red-500/20 dark:focus:ring-red-500/30",
            
            // Focus outline
            "focus:outline-none focus:shadow-lg dark:focus:shadow-lg dark:focus:shadow-theme/20",
            
            // Remove default appearance
            "file:border-0 file:bg-transparent file:text-sm file:font-medium",
            
            className
          )}
          {...props}
        />
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
        )}
        {helperText && !error && (
          <p className="text-sm text-app-2 dark:text-gray-400 mt-1">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
